import { useState } from "react";
import { Plus } from "lucide-react";
import type { CustomFieldType } from "@hub/shared";
import { Button, Field, Input, Pill, Select, Spinner, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import s from "../shared.module.css";

const TYPES: { id: CustomFieldType; label: string; hint: string }[] = [
  { id: "text", label: "Text", hint: "A short line of text" },
  { id: "number", label: "Number", hint: "Whole or decimal numbers" },
  { id: "date", label: "Date", hint: "A calendar date" },
  { id: "select", label: "Choice", hint: "One of a fixed list" },
  { id: "multiselect", label: "Several choices", hint: "Any number from a fixed list" },
  { id: "url", label: "Link", hint: "A web address" },
  { id: "checkbox", label: "Yes or no", hint: "A checkbox" },
];

/** Turns a display name into the stored field name: lower case, spaces to underscores, ascii only. */
export function fieldNameFor(displayName: string): string {
  return displayName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

/** Settings > Fields: the custom fields every person can carry. Fields are workspace wide and searchable. */
export function FieldsSection() {
  const defs = useEngineQuery("getCustomFieldDefinitions", undefined);
  const define = useEngineMutation("defineCustomField", { invalidate: ["getCustomFieldDefinitions", "getContact"] });
  const [adding, setAdding] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [type, setType] = useState<CustomFieldType>("text");
  const [options, setOptions] = useState("");
  const needsOptions = type === "select" || type === "multiselect";
  const optionList = options.split(/[,\n]/).map((o) => o.trim()).filter(Boolean);
  const taken = (defs.data ?? []).some((d) => d.fieldName === fieldName);
  const valid = displayName.trim().length > 0 && fieldName.length > 0 && (!needsOptions || optionList.length > 0);

  const reset = () => { setAdding(false); setDisplayName(""); setFieldName(""); setNameTouched(false); setType("text"); setOptions(""); };
  const submit = () => {
    if (!valid) return;
    define.mutate({ fieldName, displayName: displayName.trim(), fieldType: type, options: needsOptions ? optionList : [] }, { onSuccess: (d) => { toast(`${taken ? "Updated" : "Added"} the field ${d.displayName}`, "success"); reset(); } });
  };

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 650 }}>Fields</h2>
        <div className="u-small u-muted">Custom fields appear on every person, in the editor and in search (custom:value).</div>
      </div>
      {defs.isLoading && <Spinner />}
      {(defs.data ?? []).map((d) => (
        <div key={d.fieldName} className={s.settingRow}>
          <div>
            <div className={s.settingLabel}>{d.displayName}</div>
            <div className={s.settingHint}>
              <code className="u-mono">{d.fieldName}</code>
              {d.options.length > 0 && <span>: {d.options.join(", ")}</span>}
            </div>
          </div>
          <Pill>{TYPES.find((t) => t.id === d.fieldType)?.label ?? d.fieldType}</Pill>
        </div>
      ))}
      {defs.isSuccess && defs.data.length === 0 && !adding && <div className="u-small u-muted" style={{ padding: "8px 0" }}>No custom fields yet. Add one for anything Hub does not track on its own: how you met, a client number, a dietary note.</div>}
      {adding ? (
        <form className="u-col" style={{ gap: 10, marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 8 }} onSubmit={(e) => { e.preventDefault(); submit(); }} data-testid="field-form">
          <div className="u-row" style={{ gap: 12, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <Field label="Display name"><Input autoFocus value={displayName} onChange={(e) => { setDisplayName(e.target.value); if (!nameTouched) setFieldName(fieldNameFor(e.target.value)); }} placeholder="How we met" /></Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field label="Field name" hint={taken ? "Exists: saving updates its type and options." : "Used in search and exports."}><Input value={fieldName} onChange={(e) => { setNameTouched(true); setFieldName(fieldNameFor(e.target.value)); }} className="u-mono" /></Field>
            </div>
          </div>
          <Field label="Type" hint={TYPES.find((t) => t.id === type)?.hint}>
            <Select value={type} onChange={(e) => setType(e.target.value as CustomFieldType)}>
              {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </Select>
          </Field>
          {needsOptions && <Field label="Options" hint="Comma separated"><Input value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Conference, Introduction, Online" /></Field>}
          <div className="u-row" style={{ justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={reset}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={!valid} loading={define.isPending}>{taken ? "Update field" : "Add field"}</Button>
          </div>
        </form>
      ) : (
        <Button size="sm" icon={<Plus size={13} />} style={{ marginTop: 12 }} onClick={() => setAdding(true)}>New field</Button>
      )}
    </>
  );
}
